import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../LanguageContext';
import nwacLogo from '../assets/images/NWAC_logo.png';

const About = () => {
  const { language } = useContext(LanguageContext);

  const text = {
    en: {
      about: "About the NWAC ISET Portal",
      mission: "What this portal is designed to do",
      coreValues: "What you can expect",
      solutions: "Supports that may be available",
      excellence: "Privacy and respectful handling of information",
      contact: "Questions or support",
      aboutContent: "The Native Women’s Association of Canada (NWAC) created this Indigenous Skills and Employment Training (ISET) portal so you can apply online, check updates, and communicate securely with the program team.",
      missionContent: "The portal guides you step by step, lets you save and return later, and explains why information is being requested. You can share your goals and upload documents in one secure place.",
      coreValuesContent: [
        "Step-by-step guidance: prompts explain why information is requested and how it supports eligibility review or service delivery.",
        "Save and return: you can pause your application and continue when it feels manageable.",
        "Accessibility by design: pages are built and tested against WCAG 2.2 AA for keyboard navigation, colour contrast, and screen-reader support.",
        "Secure submission: every form uses encrypted connections and layered protections before information reaches authorized program staff."
      ],
      solutionsContent: "After review, NWAC's ISET team may work with you on supports such as:",
      solutionsList: [
        "Training costs, tuition, or materials that fit your employment or education plan.",
        "Employment counselling, coaching, and job search support.",
        "Child care, transportation, or other approved supports that help you take part in training or work."
      ],
      excellenceContent: "Awentech operates the portal on Canadian cloud infrastructure that follows the Canadian Centre for Cyber Security (CCCS) Medium Cloud security profile. Your information stays in Canada and is accessed only by authorized NWAC staff and authorized delivery partners who need it to support your application.",
      contactContent: "If you need help with an application or have a case-specific question,",
      contactLinkLabel: "use secure Messages",
      contactPortalNote: "after signing in, or visit the",
      contactExternalLabel: "NWAC contact page",
      contactExternalSuffix: " for national program enquiries."
    },
    fr: {
      about: "\u00C0 propos du portail ISET de l'AFAC",
      mission: "Ce que le portail est con\u00E7u pour faire",
      coreValues: "Ce \u00E0 quoi vous pouvez vous attendre",
      solutions: "Soutiens qui peuvent \u00EAtre offerts",
      excellence: "Confidentialit\u00E9 et traitement respectueux des renseignements",
      contact: "Questions ou soutien",
      aboutContent: "L'Association des femmes autochtones du Canada (AFAC) a cr\u00E9\u00E9 ce portail du programme ISET afin que vous puissiez pr\u00E9senter une demande en ligne, suivre les mises \u00E0 jour et communiquer de fa\u00E7on s\u00E9curis\u00E9e avec l'\u00E9quipe du programme.",
      missionContent: "Le portail vous guide \u00E9tape par \u00E9tape, vous permet d'enregistrer votre progression et de revenir plus tard, et explique pourquoi certains renseignements sont demand\u00E9s. Vous pouvez y pr\u00E9senter vos objectifs et t\u00E9l\u00E9verser vos documents dans un m\u00EAme espace s\u00E9curis\u00E9.",
      coreValuesContent: [
        "Accompagnement \u00E9tape par \u00E9tape : les messages expliquent pourquoi certains renseignements sont demand\u00E9s et comment ils soutiennent l'examen de l'admissibilit\u00E9 ou des services.",
        "Sauvegarde et reprise : vous pouvez interrompre votre demande et la reprendre lorsqu'elle vous semble plus facile \u00E0 poursuivre.",
        "Exp\u00E9rience accessible : les pages sont con\u00E7ues et test\u00E9es pour respecter WCAG 2.2 niveau AA, y compris la navigation au clavier, le contraste et la compatibilit\u00E9 avec les lecteurs d'\u00E9cran.",
        "Transmission s\u00E9curis\u00E9e : chaque formulaire utilise des connexions chiffr\u00E9es et plusieurs niveaux de protection avant d'\u00EAtre re\u00E7u par le personnel autoris\u00E9 du programme."
      ],
      solutionsContent: "Apr\u00E8s l'examen de votre demande, l'\u00E9quipe ISET de l'AFAC peut travailler avec vous pour coordonner des soutiens, par exemple :",
      solutionsList: [
        "Frais de formation, droits de scolarit\u00E9 ou mat\u00E9riel correspondant \u00E0 votre projet d'emploi ou d'\u00E9tudes.",
        "Conseils en emploi, accompagnement et aide \u00E0 la recherche d'un poste.",
        "Garde d'enfants, transport ou autres soutiens approuv\u00E9s qui facilitent votre participation \u00E0 la formation ou au travail."
      ],
      excellenceContent: "Awentech exploite le portail sur une infrastructure nuagique canadienne conforme au profil de s\u00E9curit\u00E9 infonuagique de niveau moyen du Centre canadien pour la cybers\u00E9curit\u00E9 (CCCS). Vos renseignements demeurent au Canada et ne sont accessibles qu'au personnel autoris\u00E9 de l'AFAC et aux partenaires de prestation autoris\u00E9s qui en ont besoin pour soutenir votre demande.",
      contactContent: "Si vous avez besoin d'aide pour une demande ou si vous avez une question propre a votre dossier,",
      contactLinkLabel: "utilisez les Messages securises",
      contactPortalNote: "apres l'ouverture de session, ou visitez la",
      contactExternalLabel: "page de contact de l'AFAC",
      contactExternalSuffix: " pour des questions sur les programmes nationaux."
    }
  };

  return (
    <main className="govuk-main-wrapper govuk-width-container" role="main">
      <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
        <img src={nwacLogo} alt="NWAC logo" width="100" height="100" />
      </div>
      <h1 className="govuk-heading-l">{text[language].about}</h1>
      <p className="govuk-body">{text[language].aboutContent}</p>

      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].mission}</h2>
        <p className="govuk-body">{text[language].missionContent}</p>
      </section>

      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].coreValues}</h2>
        <ul className="govuk-list govuk-list--bullet">
          {text[language].coreValuesContent.map((value, index) => (
            <li key={index}>{value}</li>
          ))}
        </ul>
      </section>

      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].solutions}</h2>
        <p className="govuk-body">{text[language].solutionsContent}</p>
        <ul className="govuk-list govuk-list--bullet">
          {text[language].solutionsList.map((solution, index) => (
            <li key={index}>{solution}</li>
          ))}
        </ul>
      </section>

      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].excellence}</h2>
        <p className="govuk-body">{text[language].excellenceContent}</p>
      </section>

      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].contact}</h2>
        <p className="govuk-body">
          {text[language].contactContent}{' '}
          <Link to="/messages/new" className="govuk-link">{text[language].contactLinkLabel}</Link>{' '}
          {text[language].contactPortalNote}{' '}
          <a href="https://nwac.ca/contact-us" target="_blank" rel="noreferrer" className="govuk-link">
            {text[language].contactExternalLabel}
          </a>
          {text[language].contactExternalSuffix}
        </p>
      </section>
    </main>
  );
};

export default About;




